import React, { useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { JobFilters as JobFiltersType } from "../../types/job";

interface JobFiltersProps {
  filters: JobFiltersType;
  onFilterChange: (filters: JobFiltersType) => void;
}

const JobFilters: React.FC<JobFiltersProps> = ({ filters, onFilterChange }) => {
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "experience",
    "jobType",
  ]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  const updateFilter = (
    key: keyof JobFiltersType,
    value: string | string[]
  ) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const clearAllFilters = () => {
    onFilterChange({
      location: "",
      experience: "",
      jobType: "",
      salary: "",
      company: "",
      skills: [],
    });
  };

  const hasActiveFilters = Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== ""
  );

  const FilterSection: React.FC<{
    title: string;
    sectionKey: string;
    children: React.ReactNode;
  }> = ({ title, sectionKey, children }) => (
    <div className="border-b border-gray-200 pb-4">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="flex items-center justify-between w-full text-left font-medium text-gray-900 mb-3"
      >
        {title}
        <ChevronDown
          className={`w-4 h-4 transition-transform ${
            expandedSections.includes(sectionKey) ? "rotate-180" : ""
          }`}
        />
      </button>
      {expandedSections.includes(sectionKey) && children}
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">필터</h3>
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            모두 지우기
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* 경력 */}
        <FilterSection title="경력" sectionKey="experience">
          <div className="space-y-2">
            {[
              { value: "신입", label: "신입 (0-1년)" },
              { value: "1-2년", label: "1-2년" },
              { value: "3-5년", label: "3-5년" },
              { value: "5-7년", label: "5-7년" },
              { value: "7년 이상", label: "7년 이상" },
            ].map((option) => (
              <label key={option.value} className="flex items-center">
                <input
                  type="radio"
                  name="experience"
                  value={option.value}
                  checked={filters.experience === option.value}
                  onChange={(e) => updateFilter("experience", e.target.value)}
                  className="mr-3 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        {/* 고용 형태 */}
        <FilterSection title="고용 형태" sectionKey="jobType">
          <div className="space-y-2">
            {[
              { value: "정규직", label: "정규직" },
              { value: "계약직", label: "계약직" },
              { value: "인턴", label: "인턴" },
              { value: "프리랜서", label: "프리랜서" },
              { value: "아르바이트", label: "아르바이트" },
            ].map((option) => (
              <label key={option.value} className="flex items-center">
                <input
                  type="radio"
                  name="jobType"
                  value={option.value}
                  checked={filters.jobType === option.value}
                  onChange={(e) => updateFilter("jobType", e.target.value)}
                  className="mr-3 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        {/* 지역 */}
        <FilterSection title="지역" sectionKey="location">
          <div className="space-y-2">
            {[
              "서울",
              "경기",
              "인천",
              "부산",
              "대구",
              "광주",
              "대전",
              "울산",
              "세종",
              "강원",
              "충북",
              "충남",
              "전북",
              "전남",
              "경북",
              "경남",
              "제주",
              "원격근무",
            ].map((location) => (
              <label key={location} className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.location === location}
                  onChange={(e) =>
                    updateFilter("location", e.target.checked ? location : "")
                  }
                  className="mr-3 text-blue-600 focus:ring-blue-500 rounded"
                />
                <span className="text-sm text-gray-700">{location}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        {/* 연봉 */}
        <FilterSection title="연봉" sectionKey="salary">
          <div className="space-y-2">
            {[
              { value: "2000-3000", label: "2,000-3,000만원" },
              { value: "3000-4000", label: "3,000-4,000만원" },
              { value: "4000-5000", label: "4,000-5,000만원" },
              { value: "5000-6000", label: "5,000-6,000만원" },
              { value: "6000-8000", label: "6,000-8,000만원" },
              { value: "8000+", label: "8,000만원 이상" },
            ].map((option) => (
              <label key={option.value} className="flex items-center">
                <input
                  type="radio"
                  name="salary"
                  value={option.value}
                  checked={filters.salary === option.value}
                  onChange={(e) => updateFilter("salary", e.target.value)}
                  className="mr-3 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        {/* 기술 스택 */}
        <FilterSection title="기술 스택" sectionKey="skills">
          <div className="space-y-2">
            {[
              "React",
              "Vue.js",
              "Angular",
              "JavaScript",
              "TypeScript",
              "Node.js",
              "Python",
              "Java",
              "Spring Boot",
              "Django",
              "AWS",
              "Docker",
              "Kubernetes",
              "MongoDB",
              "MySQL",
              "Redis",
              "GraphQL",
              "REST API",
              "Git",
              "Linux",
            ].map((skill) => (
              <label key={skill} className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.skills.includes(skill)}
                  onChange={(e) => {
                    const newSkills = e.target.checked
                      ? [...filters.skills, skill]
                      : filters.skills.filter((s) => s !== skill);
                    updateFilter("skills", newSkills);
                  }}
                  className="mr-3 text-blue-600 focus:ring-blue-500 rounded"
                />
                <span className="text-sm text-gray-700">{skill}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      </div>

      {/* 활성 필터 표시 */}
      {hasActiveFilters && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            적용된 필터:
          </h4>
          <div className="flex flex-wrap gap-2">
            {filters.experience && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                경력: {filters.experience}
                <button
                  onClick={() => updateFilter("experience", "")}
                  className="ml-1 hover:text-blue-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.jobType && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                {filters.jobType}
                <button
                  onClick={() => updateFilter("jobType", "")}
                  className="ml-1 hover:text-blue-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.location && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                {filters.location}
                <button
                  onClick={() => updateFilter("location", "")}
                  className="ml-1 hover:text-blue-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.skills.map((skill) => (
              <span
                key={skill}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
              >
                {skill}
                <button
                  onClick={() =>
                    updateFilter(
                      "skills",
                      filters.skills.filter((s) => s !== skill)
                    )
                  }
                  className="ml-1 hover:text-blue-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobFilters;
